import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSearchParam } from "react-use";
import {
  useAVToggle,
  useHMSActions,
  usePreviewJoin,
} from "@100mslive/react-sdk";
import { CircleAlert, Loader2, Settings, Video } from "lucide-react";
import { ControlButton } from "@/components/common/ControlButton";
import { PageShell } from "@/components/common/PageShell";
import { StatusScreen } from "@/components/common/StatusScreen";
import {
  BlurToggle,
  CameraToggle,
  MicToggle,
} from "@/components/meeting/DeviceControls";
import { PreviewTile } from "@/components/preview/PreviewTile";
import { WhoIsHere } from "@/components/preview/WhoIsHere";
import { DevicePickers } from "@/components/settings/DevicePickers";
import { SettingsDialog } from "@/components/settings/SettingsDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePreviewPreferences } from "@/hooks/usePreferences";
import getToken, { newUserId } from "@/services/tokenService";

/**
 * Query params (kept from the 100ms template, the recording bot depends on
 * the first two):
 * - skip_preview=true, or token=beam_recording: join at once, muted, as
 *   "Beam", with no header or footer
 * - skip_preview_headful=true: join at once but keep the call chrome
 * - name=...: initial name
 * - auth_token=...: use this token instead of asking the token endpoint
 */
const tokenEndpoint = () => process.env.REACT_APP_TOKEN_GENERATION_ENDPOINT;

function describeTokenError(error) {
  const status = error?.response?.status;
  if (status === 404) {
    return {
      title: "We couldn't find this interview",
      description:
        "The link may be mistyped, or the interview may have been cancelled or rescheduled.",
    };
  }
  if (status === 403) {
    return {
      title: "This interview link is disabled",
      description: "This link can no longer be used to join the interview.",
    };
  }
  return {
    title: "We couldn't connect you",
    description:
      "Something went wrong while preparing your call. Check your connection and try again.",
  };
}

/**
 * Always shown: the 100ms interview template records every session
 * (`autoStart`), whatever the platform's `recording_enabled` says. If the
 * template stops doing that, show this only when `recording_enabled` is true.
 */
const RecordingNotice = () => (
  <p
    className="flex items-start gap-2 border-t bg-muted/50 px-5 py-3 text-xs text-muted-foreground"
    data-testid="recording_notice"
  >
    <span className="mt-1 size-2 shrink-0 rounded-full bg-destructive" />
    This interview is recorded. Everything said and shared, screen shares
    included, is part of the recording.
  </p>
);

const PreviewJoin = ({
  token,
  roomId,
  role,
  skipPreview,
  keepChrome,
  initialName,
}) => {
  const navigate = useNavigate();
  const hmsActions = useHMSActions();
  const [preferences, setPreferences] = usePreviewPreferences();
  const [name, setName] = useState(
    initialName || (skipPreview ? "Beam" : preferences.name) || ""
  );
  const [previewFailed, setPreviewFailed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { isLocalAudioEnabled, isLocalVideoEnabled } = useAVToggle();
  const { enableJoin, preview, join } = usePreviewJoin({
    // Trimmed like the saved preference, so the other side never sees
    // stray spaces around the name.
    name: name.trim(),
    token,
    initialSettings: {
      isAudioMuted: skipPreview || preferences.isAudioMuted,
      isVideoMuted: skipPreview || preferences.isVideoMuted,
    },
    captureNetworkQualityInPreview: true,
    handleError: (error, method) => {
      if (method === "preview") {
        setPreviewFailed(true);
      }
    },
  });

  const joinCall = useCallback(() => {
    if (!skipPreview) {
      setPreferences({
        name: name.trim(),
        isAudioMuted: !isLocalAudioEnabled,
        isVideoMuted: !isLocalVideoEnabled,
      });
    }
    hmsActions.setAppData("isHeadless", skipPreview && !keepChrome);
    join();
    navigate(`/meeting/${roomId}/${role}`);
  }, [
    skipPreview,
    keepChrome,
    name,
    isLocalAudioEnabled,
    isLocalVideoEnabled,
    setPreferences,
    hmsActions,
    join,
    navigate,
    roomId,
    role,
  ]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    if (skipPreview) {
      joinCall();
    } else {
      preview();
    }
  }, [skipPreview, joinCall, preview]);

  const canJoin = Boolean(name.trim()) && enableJoin;

  return (
    <Card className="w-full max-w-xl gap-0 overflow-hidden py-0">
      <form
        onSubmit={event => {
          event.preventDefault();
          if (canJoin) {
            joinCall();
          }
        }}
      >
        <div className="flex items-center justify-between gap-4 px-5 py-4">
          <div className="min-w-0">
            <h1 className="text-base font-semibold tracking-tight">
              Ready to join?
            </h1>
            <WhoIsHere />
          </div>
          <Button type="submit" disabled={!canJoin}>
            {enableJoin || !name.trim() ? null : (
              <Loader2 className="animate-spin" />
            )}
            Join interview
          </Button>
        </div>
        <PreviewTile name={name} failed={previewFailed} />
        <div className="flex items-center justify-center gap-1 border-b px-3 py-1.5">
          <MicToggle />
          <CameraToggle />
          <BlurToggle />
          <ControlButton
            data-testid="preview_setting_btn"
            label="Settings"
            caption="Settings"
            onClick={() => setSettingsOpen(true)}
          >
            <Settings />
          </ControlButton>
        </div>
        <div className="grid gap-4 px-5 py-4">
          <div className="grid gap-1.5">
            <Label htmlFor="name" className="text-xs text-muted-foreground">
              Your name
            </Label>
            <Input
              id="name"
              required
              autoFocus
              autoComplete="name"
              placeholder="Enter your name"
              value={name}
              onChange={event => setName(event.target.value)}
            />
          </div>
          <DevicePickers />
        </div>
        <RecordingNotice />
      </form>
      {settingsOpen ? (
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      ) : null}
    </Card>
  );
};

const PreviewScreen = () => {
  const { roomId, role } = useParams();
  const skipPreviewParam = useSearchParam("skip_preview");
  const tokenParam = useSearchParam("token");
  const headfulParam = useSearchParam("skip_preview_headful");
  const nameParam = useSearchParam("name");
  const authToken = useSearchParam("auth_token");
  const keepChrome = headfulParam === "true";
  const skipPreview =
    skipPreviewParam === "true" ||
    tokenParam === "beam_recording" ||
    keepChrome;

  const [token, setToken] = useState(authToken || null);
  const [tokenError, setTokenError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (authToken) {
      return undefined;
    }
    let cancelled = false;
    setTokenError(null);
    getToken(tokenEndpoint(), newUserId(), role, roomId)
      .then(value => {
        if (!cancelled) {
          setToken(value);
        }
      })
      .catch(error => {
        if (!cancelled) {
          setTokenError(error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authToken, role, roomId, attempt]);

  if (tokenError) {
    const { title, description } = describeTokenError(tokenError);
    return (
      <StatusScreen
        icon={CircleAlert}
        tone="error"
        testId="token_error"
        title={title}
        description={description}
        actions={
          <Button
            data-testid="retry_btn"
            onClick={() => setAttempt(value => value + 1)}
          >
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <PageShell>
      {token ? (
        <PreviewJoin
          token={token}
          roomId={roomId}
          role={role}
          skipPreview={skipPreview}
          keepChrome={keepChrome}
          initialName={nameParam}
        />
      ) : (
        <div className="flex max-w-sm flex-col items-center gap-4 text-center">
          <div className="flex size-11 items-center justify-center rounded-full bg-muted text-foreground">
            <Video className="size-5" />
          </div>
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            If connecting is taking longer than expected, please try refreshing
            the page
          </p>
        </div>
      )}
    </PageShell>
  );
};

export default PreviewScreen;
